import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { CartJobRow } from "@/components/CartJobRow";

const err = (e) => toast.error(e.response?.data?.detail || "Something went wrong");
const STATUS_CLS = { in_stock: "text-emerald-700", assigned: "text-sky-700", damaged: "text-amber-700", lost: "text-red-700", retired: "text-muted-foreground" };

export default function Carts() {
    const [carts, setCarts] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [newCart, setNewCart] = useState({ serial: "", size: "96gal" });
    const [job, setJob] = useState({ type: "delivery", customer_id: "", scheduled_date: new Date().toISOString().slice(0, 10), notes: "" });

    const load = () => Promise.all([api.get("/carts"), api.get("/cart-jobs")]).then(([c, j]) => { setCarts(c.data); setJobs(j.data); });
    useEffect(() => { load(); api.get("/customers", { params: { limit: 500 } }).then(({ data }) => setCustomers(data)); }, []);

    const addCart = async () => {
        try { await api.post("/carts", newCart); setNewCart({ serial: "", size: newCart.size }); toast.success("Cart added"); load(); } catch (e) { err(e); }
    };
    const addJob = async () => {
        if (!job.customer_id) return toast.error("Pick a customer");
        try { await api.post("/cart-jobs", job); toast.success("Scheduled"); setJob({ ...job, customer_id: "", notes: "" }); load(); } catch (e) { err(e); }
    };
    const setStatus = async (c, status) => { try { await api.patch(`/carts/${c.id}`, { status }); load(); } catch (e) { err(e); } };

    return (
        <div data-testid="carts-page">
            <PageHeader title="Carts" subtitle="Cart inventory by serial number, deliveries, retrievals and swaps. Cart charges are added to the customer's next invoice." />
            <div className="px-8 py-6 grid lg:grid-cols-2 gap-6">
                <Card><CardContent className="p-5 space-y-3">
                    <div className="font-heading font-semibold">Schedule a delivery / retrieval</div>
                    <div className="grid grid-cols-2 gap-2">
                        <Select value={job.type} onValueChange={(v) => setJob({ ...job, type: v })}>
                            <SelectTrigger data-testid="cartjob-type"><SelectValue /></SelectTrigger>
                            <SelectContent>{["delivery", "retrieval", "swap"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input type="date" value={job.scheduled_date} onChange={(e) => setJob({ ...job, scheduled_date: e.target.value })} data-testid="cartjob-date" />
                        <Select value={job.customer_id} onValueChange={(v) => setJob({ ...job, customer_id: v })}>
                            <SelectTrigger className="col-span-2" data-testid="cartjob-customer"><SelectValue placeholder="Customer" /></SelectTrigger>
                            <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.address?.line1}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input className="col-span-2" placeholder="Notes for the driver" value={job.notes} onChange={(e) => setJob({ ...job, notes: e.target.value })} />
                    </div>
                    <Button size="sm" onClick={addJob} data-testid="cartjob-add-btn"><Plus className="w-4 h-4 mr-1" />Schedule</Button>
                    <div className="divide-y border rounded" data-testid="cartjob-list">
                        {jobs.filter((j) => j.status === "scheduled").map((j) => <CartJobRow key={j.id} job={j} onDone={load} />)}
                        {!jobs.some((j) => j.status === "scheduled") && <div className="p-3 text-xs text-muted-foreground">No open cart jobs</div>}
                    </div>
                    <details className="text-xs"><summary className="cursor-pointer text-muted-foreground">Completed jobs ({jobs.filter((j) => j.status === "completed").length})</summary>
                        {jobs.filter((j) => j.status === "completed").map((j) => (
                            <div key={j.id} className="py-1 flex gap-2"><b>{j.type}</b>{j.customer_name}<span className="font-mono">{j.serial}</span>
                                {j.charge_amount && <span>· ${j.charge_amount} added to invoice</span>}
                                {j.photo_url && <button className="text-primary underline" data-testid={`cartjob-photo-link-${j.id}`}
                                    onClick={async () => { const { data } = await api.get(j.photo_url.replace(/^\/api/, ""), { responseType: "blob" }); window.open(URL.createObjectURL(data), "_blank"); }}>photo</button>}</div>
                        ))}
                    </details>
                </CardContent></Card>
                <Card><CardContent className="p-5 space-y-3">
                    <div className="font-heading font-semibold">Cart inventory ({carts.length})</div>
                    <div className="flex gap-2">
                        <Input placeholder="Serial #" value={newCart.serial} onChange={(e) => setNewCart({ ...newCart, serial: e.target.value })} data-testid="cart-serial-input" />
                        <Select value={newCart.size} onValueChange={(v) => setNewCart({ ...newCart, size: v })}>
                            <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                            <SelectContent>{["32gal", "64gal", "96gal", "2yd", "4yd"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button size="sm" onClick={addCart} disabled={!newCart.serial} data-testid="cart-add-btn">Add</Button>
                    </div>
                    <div className="max-h-[60vh] overflow-y-auto divide-y border rounded" data-testid="cart-list">
                        {carts.map((c) => (
                            <div key={c.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`cart-row-${c.serial}`}>
                                <span className="font-mono w-24">{c.serial}</span><span className="text-xs w-14">{c.size}</span>
                                <span className={`text-xs w-20 ${STATUS_CLS[c.status]}`}>{c.status.replace("_", " ")}</span>
                                <span className="text-xs flex-1 truncate">{c.customer_name || ""}</span>
                                <Select value={c.status} onValueChange={(v) => setStatus(c, v)}>
                                    <SelectTrigger className="h-7 w-28 text-xs"><SelectValue /></SelectTrigger>
                                    <SelectContent>{["in_stock", "assigned", "damaged", "lost", "retired"].map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        ))}
                    </div>
                </CardContent></Card>
            </div>
        </div>
    );
}
